import { ColumnsType } from "antd/es/table";
import React, { useContext, useEffect, useState } from "react";
import { FileProps, PaginationProps } from "../../../interfaces/interfaces";
import { Avatar, Button, Space, Tooltip } from "antd";
import { FileOutlined, DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import DataContext from "../../../context/DataContext";
import { Table, TablePaginationConfig } from "antd/lib";
import { FilterValue } from "antd/es/table/interface";
import Link from "antd/es/typography/Link";

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Record<string, FilterValue>;
}

export const FilesTable = ({
  idClient,
  files,
  onChangePage,
}: {
  idClient: string;
  files: {
    data: FileProps[];
    pagination: PaginationProps;
  };
  onChangePage: (current: number, pageSize: number) => void;
}) => {
  const [tableParams, setTableParams] = useState<TableParams>({});
  const { dataLoading, downloadFile } = useContext(DataContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    onChangePage(currentPage, pageSize);
  }, [currentPage, pageSize]);

  useEffect(() => {
    setTableParams({
      pagination: {
        pageSize: pageSize,
        showSizeChanger: true,
        pageSizeOptions: ["10", "25", "50", "100"],
        showTotal: (total) => `Total ${total} Tasks`,
        position: ["bottomCenter"],
        total: parseInt(files.pagination?.total) || 0,
        onChange: (page, pageSize) => {
          setCurrentPage(page);
          setTableParams((prev) => {
            return {
              ...prev,
              pagination: {
                ...prev.pagination,
                pageSize: pageSize,
              },
            };
          });
          setPageSize(pageSize);
        },
      },
    });
  }, [files.pagination]);

  const columns: ColumnsType<FileProps> = [
    {
      title: "Name",
      dataIndex: "customName",
      key: "customName",
      width: "30%",
      render: (text, record) => (
        <Space>
          <Avatar icon={<FileOutlined />} size={"small"} />
          <span
            style={{
              textDecoration: "underline",
              color: "#1890ff",
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: "14px",
              textOverflow: "ellipsis",
              wordWrap: "break-word",
            }}
            onClick={() => {
              record.path && record.path !== ""
                ? downloadFile({
                    endpoint: `file/${idClient}/${record._id}`,
                    next: () => {},
                  })
                : record.link &&
                  record.link !== "" &&
                  window.open(record.link, "_blank");
            }}
          >
            {text || record.name}
          </span>
        </Space>
      ),
      sorter: (a, b) => {
        const nameA = a.customName || a.name;
        const nameB = b.customName || b.name;
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Uploaded By",
      dataIndex: ["uploadedBy", "name"],
      key: "uploadedBy",
      render: (_, record) => record.uploadedBy?.name || "-",
      sorter: (a, b) => {
        const nameA = a.uploadedBy?.name || "";
        const nameB = b.uploadedBy?.name || "";
        return nameA.localeCompare(nameB);
      },
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Customer",
      dataIndex: ["customer", "name"],
      key: "customer",
      render: (_, record) => record.customer?.DisplayName || "-",
      sorter: (a, b) => {
        const nameA = a.customer?.DisplayName || "";
        const nameB = b.customer?.DisplayName || "";
        return nameA.localeCompare(nameB);
      },
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Project",
      dataIndex: ["project", "name"],
      key: "project",
      render: (_, record) => record.project?.name || "-",
      sorter: (a, b) => {
        const nameA = a.project?.name || "";
        const nameB = b.project?.name || "";
        return nameA.localeCompare(nameB);
      },
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Upload Date",
      dataIndex: "uploadDate",
      key: "uploadDate",
      render: (date) => dayjs(date).format("MM/DD/YYYY HH:mm"),
      responsive: ["xl", "lg"],
    },
    {
      title: "External Link",
      dataIndex: "link",
      key: "link",
      render: (link) => (
        <div
          style={{
            maxWidth: "120px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          <Link href={link} target="_blank">
            {link || "-"}
          </Link>
        </div>
      ),
      responsive: ["xl", "lg"],
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Tooltip title="Download file">
          <Button
            loading={dataLoading}
            disabled={!record.path}
            icon={<DownloadOutlined />}
            onClick={() =>
              downloadFile({
                endpoint: `file/${idClient}/${record._id}`,
                next: () => {},
              })
            }
          ></Button>
        </Tooltip>
      ),
    },
  ];
  return (
    <Table
      bordered
      className="summary-client__table"
      style={{ padding: "0 10px" }}
      columns={columns}
      dataSource={files.data}
      pagination={tableParams.pagination}
      size="small"
      rowKey={"_id"}
      tableLayout="auto"
    />
  );
};
